import React, { useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Lottie from "lottie-react";
// FIXED: Corrected import path to match the file 'questionnaireStore.js'
import useQuestionnaireStore from "../store/useQuestionnaireStore";
// FIXED: Corrected import path for the toast hook
import useWarning from "../hooks/useWarningModal";
import { ChevronLeft } from "lucide-react";
import Button from "../components/common/Button";

// Import your Lottie animation files
import babyAnimation from "../assets/animations/baby.json";
import kidAnimation from "../assets/animations/Kid.json";
import teenagerAnimation from "../assets/animations/teenage.json";
import officePersonAnimation from "../assets/animations/Person.json";
import seniorAnimation from "../assets/animations/old_man.json";

const ageData = [
  { range: "0-5", label: "Baby", animation: babyAnimation },
  { range: "6-12", label: "Kid", animation: kidAnimation },
  { range: "13-18", label: "Teenage", animation: teenagerAnimation },
  { range: "19-40", label: "Young Adult", animation: officePersonAnimation }, 
  { range: "41-59", label: "Adult", animation: officePersonAnimation },
  { range: "60+", label: "Old Age ", animation: seniorAnimation },
];

const genderOptions = ["Male", "Female", "Other"];

const Step2_Age = () => {
  const navigate = useNavigate();
  // FIXED: Changed 'setAgeRange' to 'setAge' to match the store definition
  const { age, setAge, gender, setGender } = useQuestionnaireStore();
  const showWarning = useWarning();

  // Determine which animation to show based on selected age
  const currentAnimation = useMemo(() => {
    const selectedOption = ageData.find((opt) => opt.range === age);
    return selectedOption ? selectedOption.animation : seniorAnimation;
  }, [age]);

  // new: ref for the scrolling container and previous-ready guard
  const containerRef = useRef(null);
  const prevReadyRef = useRef(false);

  // new: when both age and gender become selected, scroll to bottom once
  useEffect(() => {
    const ready = Boolean(age && gender);
    if (ready && !prevReadyRef.current) {
      const el = containerRef.current;
      if (el && typeof el.scrollTo === "function") {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      } else {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "smooth",
        });
      }
    }
    prevReadyRef.current = ready;
  }, [age, gender]);

  const handleNext = () => {
    if (!age) {
      showWarning("Please select your age range.");
      return;
    }
    if (!gender) {
      showWarning("Please select your gender.");
      return;
    }
    console.log("Age:", age, "Gender:", gender);
    navigate("/step-3");
  };

  return (
    <motion.div
      ref={containerRef} // attach ref here
      className="page step2-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      // FIXED: Added inline style to prevent header cutoff on smaller screens
      style={{
        justifyContent: "flex-start",
        paddingTop: "5px",
        overflowY: "auto",
      }}
    >
      <div className="step-header">
        <button onClick={() => navigate(-1)} className="back-button">
          <ChevronLeft size={24} /> Back
        </button>
        <h2>Tell us a bit about yourself</h2>
      </div>

      <div className="demographics-layout">
        {/* LEFT COLUMN: Age Selection */}
        <div className="column left-col">
          <h3>Select Age Range</h3>
          <div className="options-list">
            {ageData.map((option, index) => (
              <button
                // FIXED: Combined range and index to guarantee unique key
                key={`${option.range}-${index}`}
                className={`option-card ${
                  age === option.range ? "selected" : ""
                }`}
                // FIXED: Used 'setAge' to match store action
                onClick={() => setAge(option.range)}
              >
                <span className="label">{option.label} ({option.range}) </span>
                <span className="radio-circle"></span>
              </button>
            ))}
          </div>
        </div>

        {/* RIGHT COLUMN: Gender & Animation */}
        <div className="column right-col">
          <h3>Select Gender</h3>

          {/* Gender Buttons */}
          <div className="gender-row">
            {genderOptions.map((opt, index) => (
              <button
                // FIXED: Added index to ensure uniqueness
                key={`${opt}-${index}`}
                className={`gender-btn ${gender === opt ? "selected" : ""}`}
                onClick={() => setGender(opt)}
              >
                {opt}
              </button>
            ))}
          </div>

          {/* Animated Character */}
          <div className="animation-wrapper">
            <Lottie
              animationData={currentAnimation}
              loop={true}
              className="lottie-anim"
            />
          </div>
        </div>
      </div>

      <div className="navigation-buttons">
        <Button onClick={handleNext}>Next</Button>
      </div>
    </motion.div>
  );
};

export default Step2_Age;
